<template>
  <transition name="fade">
    <div
      v-if="isShow"
      @click="handleClickOverlay"
      class="vc-overlay"
      id="vueSelect"
    >
      <transition name="zoom">
        <div
          v-if="isShow"
          ref="SelectDialog"
          class="vc-container"
        >
          <span class="vc-text-grid">
            <h5
              v-if="dialog.title"
              class="vc-title"
              v-html="dialog.title"
            />
            <p
              v-if="dialog.message"
              class="vc-text"
              v-html="dialog.message"
            />
          </span>
          <div
            class="vc-btn-grid is-mono"
            v-if="hasMultipleButtons"
          >
            <button
              :class="['vc-btn', { 'vc-btn--without-delimiter': button.withoutDelimiter }]"
              :key="button.value"
              @click.stop="e => handleClickButton(e, button.value)"
              type="button"
              v-for="button in dialog.buttons"
            >
              {{ button.label }}
            </button>
          </div>
          <div
            v-else
            class="vc-btn-grid"
            :class="{ isMono: !dialog.button.no || !dialog.button.yes }"
          >
            <button
              v-if="dialog.button.no"
              @click.stop="e => handleClickButton(e, false)"
              class="vc-btn left"
              type="button"
            >
              {{ dialog.button.no }}
            </button>

            <button
              v-if="dialog.button.yes"
              :disabled="dialog.auth ? !password : false"
              @click.stop="e => handleClickButton(e, true)"
              class="vc-btn"
              type="button"
            >
              {{ dialog.button.yes }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import Vue from 'vue';
import { events } from './events';

Vue.directive('focus', {
  inserted(el) {
    el.focus();
  },
});

export default {
  name: 'SelectDialog',
  data() {
    return {
      isShow: false,
      dialog: {
        title: '',
        message: '',
        buttons: [],
        button: {},
      },
      params: {},
    };
  },
  computed: {
    hasMultipleButtons() {
      return this.dialog.buttons.length > 0;
    },
  },
  methods: {
    resetState() {
      this.dialog = {
        title: '',
        message: '',
        buttons: [],
        button: {},
        callback: () => {},
      };
    },
    handleClickButton(e, value) {
      if (e.target.id === 'vueSelect') return;
      this.closeDialog();
      this.$emit('action', value);
      if (this.params.callback) {
        this.params.callback(value);
      }
    },
    handleClickOverlay({ target }) {
      if (target.id === 'vueSelect') {
        this.closeDialog();
        if (this.params.callback) {
          this.params.callback(false);
        }
      }
    },
    handleKeyUp({ keyCode }) {
      if (keyCode === 27) {
        this.handleClickOverlay({ target: { id: 'vueSelect' } });
      }
    },
    open(params) {
      this.resetState();
      this.params = params;
      this.isShow = true;
      window.addEventListener('touchmove', this.preventScroll, { passive: false });
      Object.entries(params).forEach(([key, value]) => {
        if (typeof value === typeof this.dialog[key]) {
          this.dialog[key] = value;
        }
      });
    },
    preventScroll(e) {
      e.preventDefault();
    },
    closeDialog() {
      this.isShow = false;
      window.removeEventListener('touchmove', this.preventScroll, { passive: false });
    },
  },
  mounted() {
    if (!document) return;
    events.$on('openSelectDialog', this.open);
    events.$on('closeSelectDialog', () => {
      this.handleClickOverlay({ target: { id: 'vueSelect' } });
    });
  },
};
</script>

<style>
:root {
  --title-color: #222222;
  --message-color: #222222;
  --overlay-background-color: #097fb54d;
  --container-box-shadow: #0000004a 0px 3px 8px 0px;
  --base-background-color: #ffffff;
  --button-color: #2980b9;
  --button-background-color: #ffffff;
  --button-border-color: #cbe0ed;
  --button-background-color-disabled: #f5f5f5;
  --button-background-color-hover: #f5f5f5;
  --button-box-shadow-active: inset 0 2px 0px 0px #00000014;
  --input-background-color: #ebebeb;
  --input-background-color-hover: #dfdfdf;
  --font-size-m: 1rem;
  --font-size-s: .8rem;
  --font-weight-black: 900;
  --font-weight-bold: 700;
  --font-weight-medium: 500;
  --font-weight-normal: 400;
  --font-weight-light: 300;
}

.vc-overlay *,
.vc-overlay *::before,
.vc-overlay *::after {
  box-sizing: border-box;
  text-decoration: none;
  -webkit-touch-callout: none;
  -moz-osx-font-smoothing: grayscale;
  margin: 0;
  padding: 0;
}

.vc-title {
  color: var(--title-color);
  padding: 0 1rem;
  width: 100%;
  font-weight: var(--font-weight-black);
  text-align: center;
  font-size: var(--font-size-m);
  line-height: initial;
  margin-bottom: 5px;
}

.vc-text {
  color: var(--message-color);
  padding: 0 1rem;
  width: 100%;
  font-weight: var(--font-weight-medium);
  text-align: center;
  font-size: var(--font-size-s);
  line-height: initial;
}

.vc-overlay {
  background-color: var(--overlay-background-color);
  width: 100%;
  height: 100%;
  transition: all .1s ease-in;
  left: 0;
  top: 0;
  z-index: 999999999999;
  position: fixed;
  display: flex;
  place-content: baseline center;
  align-items: center;
}

.vc-container {
  background-color: var(--base-background-color);
  border-radius: 1rem;
  width: 286px;
  height: auto;
  display: grid;
  grid-template-rows: 1fr max-content;
  box-shadow: var(--container-box-shadow);
  user-select: none;
}

.vc-text-grid {
  padding: 1rem;
}

.vc-btn-grid {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-radius: 0 0 1rem 1rem;
  overflow: hidden;
}

.vc-btn-grid.is-mono {
  grid-template-columns: 1fr;
}

.vc-btn {
  border-radius: 0 0 1rem;
  color: var(--button-color);
  background-color: var(--button-background-color);
  border: 0;
  font-size: 1rem;
  border-top: 1px solid var(--button-border-color);
  cursor: pointer;
  font-weight: var(--font-weight-bold);
  outline: none;
  min-height: 50px;
}

.vc-btn:hover {
  background-color: var(--button-background-color-hover);
}

.vc-btn:disabled {
  background-color: var(--button-background-color-disabled);
}

.vc-btn--without-delimiter {
  border-top: unset;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .21s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.zoom-enter-active,
.zoom-leave-active {
  animation-duration: .21s;
  animation-fill-mode: both;
  animation-name: zoom;
}

.zoom-leave-active {
  animation-direction: reverse;
}

@keyframes zoom {
  0% {
    opacity: 0;
    transform: scale3d(1.1, 1.1, 1.1);
  }

  100% {
    opacity: 1;
    transform: scale3d(1, 1, 1);
  }
}
</style>
